import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { ResourceType } from "@app/resources";

/**
 * The lean, view-agnostic set of primitives the per-project action menu reads.
 * Both the classic dashboard (mapping from Resource / ProjectTileData) and the
 * Network Portfolio Management dashboard (mapping from NetworkPortfolioManagementProject)
 * project their data into this shape so they can share one menu component.
 */
export interface ProjectActionMenuModel {
  /** The project's resource id — drives Clone, Project Options, Share, Jobs and Archive. */
  projectId: number;
  /** The project name — used in the Share breadcrumb and the Jobs flyout title. */
  name: string;
  /** True for zip-code analysis projects, which use the zip jobs panel instead of the standard one. */
  isZipAnalysis: boolean;
  /** Whether the project is currently archived (toggles Archive vs Unarchive text/icon). */
  isArchived: boolean;
  /** Whether the project is mid-run (disables Archive). */
  isRunning: boolean;
  /** Whether the project is read-only (disables Archive unless already archived). */
  isReadOnly: boolean;
  /** Whether the project is queued (shows the Cancel Project Execution item). */
  isQueued: boolean;
  /** Whether the project is eligible to be published — drives the enabled/disabled state of the Publish item. */
  isPublishable: boolean;
  /** Whether the underlying summary data has loaded — Archive/Project Options stay disabled until it has. */
  dataReady: boolean;
}

/**
 * The action callbacks the menu invokes. Each consumer wires these to its own
 * refresh strategy (classic re-fetches a ProjectTileData; NPM invalidates its query).
 */
export interface ProjectActionMenuCallbacks {
  /** Archive/unarchive the project (already bound to the project's resource id and archive state). */
  onArchiveConfirm: () => Promise<void>;
  /** Publish the project with an optional comment. Pre-bound by the consumer to the correct service method.
   *  Per-project hamburger menu: PUT projects/{id}/publish (county) or PUT zipprojects/{id}/publish (zip).
   *  Bulk toolbar flow: bulkPublish/bulkPublishZipProjects. */
  onPublish?: (comment: string) => Promise<void>;
  /** Refresh project data after the Project Options panel saves changes. */
  onUpdateProjectOptions: () => Promise<void>;
}

/**
 * The router location.state consumed by the resource sharing page (`/resources/share`).
 * Mirrors the shape BulkSharing reads. Built per-consumer because the breadcrumb trail differs by view.
 */
export interface ResourceShareLocationState {
  resourceTypes: ResourceType[];
  resourceDescription: string;
  isSingleResource: boolean;
  resourceId: number;
  breadcrumbItems: Breadcrumb[];
}

/**
 * Builds the router state the resource sharing page (`/resources/share`) expects for a single project.
 * The breadcrumb back-link points at the dashboard the menu was opened from. resourceTypes is passed in
 * so the (currently hardcoded) project resource type lives in the caller — one place to update once the
 * backend surfaces the real resource type.
 */
export const buildShareState = (
  projectName: string,
  projectId: number,
  resourceTypes: ResourceType[],
  pathname: string,
): ResourceShareLocationState => ({
  breadcrumbItems: [
    {
      link: pathname,
      text: "Portfolio Dashboard",
    },
    {
      link: pathname,
      text: projectName,
    },
  ],
  isSingleResource: true,
  resourceDescription: "Project",
  resourceId: projectId,
  resourceTypes: resourceTypes,
});
